import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { useEstates } from '../../lib/useEstates';
import { useUpload } from '../../lib/useUpload';
import { Modal, Alert, UploadProgress, inputClass, labelClass, primaryBtn, secondaryBtn } from '../../components/ui';

interface SectionField {
  key: string;
  label: string;
  type: 'number' | 'text' | 'textarea' | 'select';
  placeholder?: string;
  options?: string[];
}

interface SectionDef {
  key: string;
  label: string;
  fields: SectionField[];
}

export const MONTHLY_SECTIONS: SectionDef[] = [
  {
    key: 'diesel_log', label: 'Diesel Log',
    fields: [
      { key: 'total_used_month', label: 'Total Used This Month (Liters)', type: 'number', placeholder: 'e.g. 2400' },
      { key: 'remaining_liters', label: 'Remaining Liters', type: 'number', placeholder: 'e.g. 820' },
      { key: 'notes', label: 'Usage Notes', type: 'textarea', placeholder: 'Fuel vendor, cost, delivery notes...' },
    ],
  },
  {
    key: 'ekedc_unit', label: 'EKEDC Unit',
    fields: [
      { key: 'total_used_month', label: 'Total Units Used This Month (kWh)', type: 'number', placeholder: 'e.g. 12500' },
      { key: 'remaining_units', label: 'Remaining Units (kWh)', type: 'number', placeholder: 'e.g. 3200' },
      { key: 'notes', label: 'Usage Notes', type: 'textarea', placeholder: 'Power supply notes, outages, cost...' },
    ],
  },
  {
    key: 'staff_report', label: 'Staff Report',
    fields: [
      { key: 'headcount', label: 'Active Staff Count', type: 'number', placeholder: 'e.g. 12' },
      { key: 'attendance', label: 'Attendance Summary', type: 'text', placeholder: 'e.g. 95% avg attendance' },
      { key: 'details', label: 'Performance & Coverage Notes', type: 'textarea', placeholder: 'Staff performance, attendance, coverage...' },
    ],
  },
  {
    key: 'residents_report', label: 'Residents Report',
    fields: [
      { key: 'total_complaints', label: 'Total Complaints Received', type: 'number', placeholder: 'e.g. 8' },
      { key: 'resolved', label: 'Resolved', type: 'number', placeholder: 'e.g. 6' },
      { key: 'details', label: 'Summary of Issues & Resolutions', type: 'textarea', placeholder: 'Residents issues, requests, resolutions...' },
    ],
  },
  {
    key: 'security', label: 'Security',
    fields: [
      { key: 'incidents', label: 'Incidents This Month', type: 'number', placeholder: 'e.g. 2' },
      { key: 'severity', label: 'Highest Severity', type: 'select', options: ['None', 'Low', 'Medium', 'High', 'Critical'] },
      { key: 'details', label: 'Security Summary', type: 'textarea', placeholder: 'Security events, breaches, patrols...' },
    ],
  },
  {
    key: 'maintenance', label: 'Maintenance',
    fields: [
      { key: 'tasks_completed', label: 'Tasks Completed', type: 'number', placeholder: 'e.g. 15' },
      { key: 'tasks_pending', label: 'Tasks Pending', type: 'number', placeholder: 'e.g. 3' },
      { key: 'details', label: 'Maintenance Summary', type: 'textarea', placeholder: 'Works done, repairs, outstanding items...' },
    ],
  },
  {
    key: 'monthly_kpi', label: 'Monthly KPI',
    fields: [
      { key: 'occupancy_rate', label: 'Occupancy Rate (%)', type: 'number', placeholder: 'e.g. 92' },
      { key: 'collection_rate', label: 'Collection Rate (%)', type: 'number', placeholder: 'e.g. 88' },
      { key: 'details', label: 'KPI Notes', type: 'textarea', placeholder: 'KPI figures, targets, performance...' },
    ],
  },
  {
    key: 'cleaning', label: 'Cleaning',
    fields: [
      { key: 'areas_covered', label: 'Areas Covered', type: 'text', placeholder: 'e.g. All common areas, gardens' },
      { key: 'status', label: 'Overall Status', type: 'select', options: ['Excellent', 'Good', 'Needs Improvement'] },
      { key: 'details', label: 'Notes', type: 'textarea', placeholder: 'Cleaning frequency, quality notes...' },
    ],
  },
  {
    key: 'waste_disposal', label: 'Waste Disposal',
    fields: [
      { key: 'disposals', label: 'Number of Disposals', type: 'number', placeholder: 'e.g. 12' },
      { key: 'status', label: 'Compliance Status', type: 'select', options: ['Compliant', 'Needs Attention', 'Non-Compliant'] },
      { key: 'details', label: 'Notes', type: 'textarea', placeholder: 'Disposal schedule, vendor, issues...' },
    ],
  },
  {
    key: 'fumigation', label: 'Fumigation',
    fields: [
      { key: 'areas_treated', label: 'Areas Treated', type: 'text', placeholder: 'e.g. All floors, basement' },
      { key: 'chemical_used', label: 'Chemical Used', type: 'text', placeholder: 'Product name' },
      { key: 'details', label: 'Notes', type: 'textarea', placeholder: 'Treatment summary, schedule...' },
    ],
  },
  {
    key: 'water_treatment', label: 'Water Treatment',
    fields: [
      { key: 'treatments_done', label: 'Treatments Performed', type: 'number', placeholder: 'e.g. 4' },
      { key: 'details', label: 'Notes', type: 'textarea', placeholder: 'Treatment type, readings, results...' },
    ],
  },
  {
    key: 'sewage_maintenance', label: 'Sewage Maintenance',
    fields: [
      { key: 'inspection_status', label: 'Inspection Status', type: 'select', options: ['OK', 'Needs Attention', 'Blockage', 'Overflow'] },
      { key: 'details', label: 'Notes', type: 'textarea', placeholder: 'Sewage system condition, work done...' },
    ],
  },
  {
    key: 'road_drainage', label: 'Road & Drainage',
    fields: [
      { key: 'condition', label: 'Overall Condition', type: 'select', options: ['Good', 'Fair', 'Poor', 'Critical'] },
      { key: 'details', label: 'Notes', type: 'textarea', placeholder: 'Road/drainage status, repairs needed...' },
    ],
  },
  {
    key: 'common_area', label: 'Common Area',
    fields: [
      { key: 'areas_checked', label: 'Areas Checked', type: 'text', placeholder: 'e.g. Lobby, pool, garden' },
      { key: 'status', label: 'Overall Status', type: 'select', options: ['Good', 'Needs Maintenance', 'Damaged'] },
      { key: 'details', label: 'Notes', type: 'textarea', placeholder: 'Common area condition, improvements...' },
    ],
  },
];

export interface MonthlySectionView {
  label: string;
  lines: string[];
  attachmentIndices: number[];
}

export function parseMonthlyReport(content: string, attachments: { id: number; filename: string; url: string; mimetype: string; size: number }[]): MonthlySectionView[] {
  const sections: MonthlySectionView[] = [];
  const sectionMap = new Map<string, MonthlySectionView>();

  const headerRegex = /^\[([^\]]+)\]\s*$/gm;
  let match: RegExpExecArray | null;
  const headerPositions: { label: string; start: number; end: number }[] = [];

  while ((match = headerRegex.exec(content)) !== null) {
    headerPositions.push({ label: match[1], start: match.index + match[0].length, end: -1 });
  }

  for (let i = 0; i < headerPositions.length; i++) {
    headerPositions[i].end = i + 1 < headerPositions.length ? headerPositions[i + 1].start - 1 : content.length;
  }

  for (const hp of headerPositions) {
    const body = content.slice(hp.start, hp.end).trim();
    const lines = body.split('\n').filter((l) => l.trim());
    const data: MonthlySectionView = { label: hp.label, lines, attachmentIndices: [] };
    sections.push(data);
    sectionMap.set(hp.label.toLowerCase(), data);
  }

  attachments.forEach((att, idx) => {
    const prefixMatch = att.filename.match(/^\[([^\]]+)\]\s*/);
    if (prefixMatch) {
      const sectionLabel = prefixMatch[1].toLowerCase();
      const section = sectionMap.get(sectionLabel);
      if (section) {
        section.attachmentIndices.push(idx);
      }
    }
  });

  if (sections.length === 0 && content.trim()) {
    sections.push({ label: 'Report', lines: content.split('\n').filter((l) => l.trim()), attachmentIndices: attachments.map((_, i) => i) });
  }

  return sections;
}

interface SectionState {
  fieldValues: Record<string, string>;
  files: File[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

const emptySections = (): Record<string, SectionState> =>
  Object.fromEntries(MONTHLY_SECTIONS.map((s) => [s.key, { fieldValues: {}, files: [] }]));

export default function MonthlyReportForm({ open, onClose, onSubmitted }: Props) {
  const { estates, getBlocksForEstate } = useEstates();
  const [sections, setSections] = useState<Record<string, SectionState>>(emptySections);
  const [facility, setFacility] = useState('');
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const upload = useUpload();
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const blocks = facility ? getBlocksForEstate(facility) : [];

  const toggleSection = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setField = (sectionKey: string, fieldKey: string, value: string) => {
    setSections((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        fieldValues: { ...prev[sectionKey].fieldValues, [fieldKey]: value },
      },
    }));
  };

  const setFiles = (sectionKey: string, files: File[]) => {
    setSections((prev) => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], files },
    }));
  };

  const hasContent = (sectionKey: string) => {
    const s = sections[sectionKey];
    return Object.values(s.fieldValues).some((v) => v && v.trim()) || s.files.length > 0;
  };

  const buildSectionContent = (section: SectionDef, state: SectionState): string => {
    const lines: string[] = [];
    for (const field of section.fields) {
      const val = state.fieldValues[field.key];
      if (val && val.trim()) {
        lines.push(`${field.label}: ${val.trim()}`);
      }
    }
    if (section.key === 'ekedc_unit') {
      if (state.fieldValues['block']) lines.push(`Block: ${state.fieldValues['block']}`);
      if (state.fieldValues['common_area']) lines.push(`Common Area: ${state.fieldValues['common_area']}`);
    }
    return lines.join('\n');
  };

  const buildFullContent = (): string => {
    return MONTHLY_SECTIONS
      .filter((s) => hasContent(s.key))
      .map((s) => {
        const body = buildSectionContent(s, sections[s.key]);
        return `[${s.label}]\n${body}`;
      })
      .join('\n\n');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!facility) {
      setError('Please select a facility.');
      return;
    }
    const anyFilled = MONTHLY_SECTIONS.some((s) => hasContent(s.key));
    if (!anyFilled) {
      setError('Please fill in at least one section.');
      return;
    }
    upload.reset();
    setError('');
    try {
      const content = buildFullContent();
      const fd = new FormData();
      fd.append('report_type', 'monthly');
      fd.append('facility', facility);
      fd.append('content', content);
      fd.append('report_date', reportDate);
      fd.append('heading', 'Monthly Report');
      MONTHLY_SECTIONS.forEach((s) => {
        sections[s.key].files.forEach((f) => {
          const prefixed = new File([f], `[${s.label}] ${f.name}`, { type: f.type });
          fd.append('attachments', prefixed);
        });
      });
      await upload.run('/reports', fd);
      onSubmitted();
      onClose();
      resetForm();
      // resetForm clears the fields; the progress bar has already reached
      // 'done' and the modal is closing, so nothing further is needed here.
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const resetForm = () => {
    setSections(emptySections());
    setFacility('');
    setReportDate(new Date().toISOString().slice(0, 10));
    setExpanded({});
    setError('');
  };

  return (
    <Modal open={open} onClose={() => { resetForm(); onClose(); }} title="Monthly Report">
      {error && <Alert>{error}</Alert>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className={labelClass}>Facility / Estate *</label>
          <select className={inputClass} required value={facility} onChange={(e) => setFacility(e.target.value)}>
            <option value="">Select facility...</option>
            {estates.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Report Date</label>
          <input type="date" className={inputClass} value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {MONTHLY_SECTIONS.map((section) => {
          const isOpen = expanded[section.key];
          const filled = hasContent(section.key);
          const state = sections[section.key];
          return (
            <div key={section.key} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection(section.key)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition text-left"
              >
                <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
                  <FileText className="h-4 w-4 text-gray-400" />
                  {section.label}
                  {filled && <span className="w-2 h-2 rounded-full bg-primary inline-block" />}
                </span>
                {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              </button>
              {isOpen && (
                <div className="p-4 space-y-3 bg-white">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {section.fields.map((field) => {
                      if (field.type === 'textarea') {
                        return (
                          <div key={field.key} className="sm:col-span-2">
                            <label className={labelClass}>{field.label}</label>
                            <textarea
                              className={inputClass}
                              rows={3}
                              value={state.fieldValues[field.key] || ''}
                              onChange={(e) => setField(section.key, field.key, e.target.value)}
                              placeholder={field.placeholder}
                            />
                          </div>
                        );
                      }
                      if (field.type === 'select') {
                        return (
                          <div key={field.key}>
                            <label className={labelClass}>{field.label}</label>
                            <select
                              className={inputClass}
                              value={state.fieldValues[field.key] || ''}
                              onChange={(e) => setField(section.key, field.key, e.target.value)}
                            >
                              <option value="">Select...</option>
                              {field.options?.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                        );
                      }
                      return (
                        <div key={field.key}>
                          <label className={labelClass}>{field.label}</label>
                          <input
                            type={field.type}
                            className={inputClass}
                            value={state.fieldValues[field.key] || ''}
                            onChange={(e) => setField(section.key, field.key, e.target.value)}
                            placeholder={field.placeholder}
                          />
                        </div>
                      );
                    })}
                  </div>
                  {section.key === 'ekedc_unit' && blocks.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Block</label>
                        <select
                          className={inputClass}
                          value={state.fieldValues['block'] || ''}
                          onChange={(e) => setField(section.key, 'block', e.target.value)}
                        >
                          <option value="">Select block...</option>
                          {blocks.map((b) => <option key={b} value={`Block ${b}`}>Block {b}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Common Area</label>
                        <select
                          className={inputClass}
                          value={state.fieldValues['common_area'] || ''}
                          onChange={(e) => setField(section.key, 'common_area', e.target.value)}
                        >
                          <option value="">Select...</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className={labelClass}>Upload Media (up to 15)</label>
                    <input
                      type="file"
                      multiple
                      max={15}
                      accept="image/*,video/*,.pdf"
                      className={inputClass}
                      onChange={(e) => {
                        const selected = Array.from(e.target.files || []);
                        setFiles(section.key, selected.slice(0, 15));
                      }}
                    />
                    {state.files.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">{state.files.length} / 15 file(s) selected</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <UploadProgress
          percent={upload.percent}
          phase={upload.phase}
          error={upload.error}
          onCancel={upload.cancel}
        />
        <div className="flex justify-end space-x-3 pt-3">
          <button type="button" onClick={() => { resetForm(); onClose(); }} className={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={upload.busy} className={primaryBtn}>
            {upload.phase === 'uploading'
              ? `Uploading ${upload.percent}%`
              : upload.phase === 'processing' ? 'Submitting...' : 'Submit Monthly Report'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
